import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { pinTileFirst } from "../lib/serviceTiles";

// Module-level cache so multiple grids share one GET /auth/tile-order call.
let _ordersPromise = null;
const fetchOrders = () => {
  if (!_ordersPromise) {
    _ordersPromise = api.get("/auth/tile-order")
      .then((r) => r.data.tile_orders || {})
      .catch(() => ({}));
  }
  return _ordersPromise;
};
export const invalidateTileOrders = () => { _ordersPromise = null; };

const applyOrder = (tiles, order) => {
  if (!order || !order.length) return tiles;
  const idx = Object.fromEntries(order.map((to, i) => [to, i]));
  return [...tiles].sort((a, b) => {
    const ia = idx[a.to] ?? 999, ib = idx[b.to] ?? 999;
    return ia - ib;
  });
};

/**
 * Long-press reorderable tile grid (all system users).
 * Long-press any tile → edit mode (tiles jiggle) → drag tiles to rearrange →
 * Done saves the order to the user's account (synced across devices).
 *
 * Props:
 *   tiles       array of tile objects (each MUST have a unique `to`)
 *   orderKey    persistence key, e.g. "quick" or "services-marketplace"
 *   className   grid classes
 *   renderTile  (tile) => JSX
 */
export default function ReorderableGrid({ tiles, orderKey, className, renderTile, pinnedTo }) {
  const [order, setOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`icaard_tile_order_${orderKey}`)) || null; }
    catch { return null; }
  });
  const [edit, setEdit] = useState(false);
  const dragIdx = useRef(null);
  const pressTimer = useRef(null);
  const startXY = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => () => clearTimeout(pressTimer.current), []);

  useEffect(() => {
    let alive = true;
    fetchOrders().then((all) => {
      if (alive && all[orderKey]) {
        setOrder(all[orderKey]);
        localStorage.setItem(`icaard_tile_order_${orderKey}`, JSON.stringify(all[orderKey]));
      }
    });
    return () => { alive = false; };
  }, [orderKey]);

  const ordered = pinTileFirst(applyOrder(tiles, order), pinnedTo);

  const save = async () => {
    const newOrder = ordered.map((t) => t.to);
    setEdit(false);
    localStorage.setItem(`icaard_tile_order_${orderKey}`, JSON.stringify(newOrder));
    try {
      await api.post("/auth/tile-order", { key: orderKey, order: newOrder });
      invalidateTileOrders();
      toast.success("Tile arrangement saved ✅");
    } catch { toast.error("Could not save the arrangement. try again."); }
  };

  const clearPress = () => { clearTimeout(pressTimer.current); pressTimer.current = null; };

  const onDown = (e, i) => {
    startXY.current = { x: e.clientX, y: e.clientY };
    if (edit) {
      if (ordered[i]?.to === pinnedTo) return;
      dragIdx.current = i;
      e.currentTarget.setPointerCapture?.(e.pointerId);
      return;
    }
    clearPress();
    pressTimer.current = setTimeout(() => {
      setEdit(true);
      try { navigator.vibrate?.(40); } catch {}
      toast.info("Rearrange mode. drag tiles, then tap Done", { duration: 4000 });
    }, 550);
  };

  const onMove = (e, i) => {
    if (!edit) {
      if (pressTimer.current && startXY.current) {
        const dx = Math.abs(e.clientX - startXY.current.x);
        const dy = Math.abs(e.clientY - startXY.current.y);
        if (dx > 10 || dy > 10) clearPress();   // user is scrolling
      }
      return;
    }
    if (dragIdx.current === null) return;
    const el = document.elementFromPoint(e.clientX, e.clientY)?.closest("[data-reorder-idx]");
    if (!el || el.closest("[data-reorder-grid]") !== containerRef.current) return;
    const over = Number(el.getAttribute("data-reorder-idx"));
    if (Number.isNaN(over) || over === dragIdx.current) return;
    if (ordered[over]?.to === pinnedTo) return;
    const arr = ordered.map((t) => t.to);
    const [moved] = arr.splice(dragIdx.current, 1);
    arr.splice(over, 0, moved);
    dragIdx.current = over;
    setOrder(arr);
  };

  const onUp = () => { clearPress(); dragIdx.current = null; };

  return (
    <div data-reorder-grid ref={containerRef} data-testid={`reorder-grid-${orderKey}`}>
      {edit && (
        <div className="flex items-center justify-between gap-2 mb-2.5 rounded-xl bg-[#7C3AED]/10 border border-[#7C3AED]/40 px-3 py-2"
             data-testid={`reorder-bar-${orderKey}`}>
          <span className="text-[11px] font-bold text-[#A78BFA]">↕ Drag tiles to rearrange</span>
          <button onClick={save} data-testid={`reorder-done-${orderKey}`}
                  className="px-4 py-1.5 rounded-full bg-[#7C3AED] text-white text-[11px] font-black">
            Done
          </button>
        </div>
      )}
      <div className={className}>
        {ordered.map((t, i) => (
          <div key={t.to} data-reorder-idx={i}
               onPointerDown={(e) => onDown(e, i)}
               onPointerMove={(e) => onMove(e, i)}
               onPointerUp={onUp}
               onPointerCancel={onUp}
               onContextMenu={(e) => { if (edit || pressTimer.current) e.preventDefault(); }}
               className={edit ? "tile-jiggle select-none" : ""}
               style={edit ? { touchAction: "none", cursor: "grab" } : undefined}>
            <div className={edit ? "pointer-events-none" : ""}>{renderTile(t)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
